import { motion } from "framer-motion";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useIsClinicOpsUser } from "@/app/hooks/useIsClinicOpsUser";
import { useLogoutAction } from "@/app/hooks/ui_hooks/useLogoutAction";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useRouter } from "@/app/hooks/useRouter";
import { VMS_CLICK_EVENTS, VMS_SCREEN_IDS, useVMSClickLog } from "@/app/hooks/useVMSClickLog";
import { useTriggerState } from "@/app/hooks/useTriggerState";
import { NAVIGATION_ROUTES } from "@/app/routes";
import { CustomerProfileBookmarkType } from "@/app/routes/auth_routes/CustomerProfilePage/CustomerDetailsPage";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import {
  CalendarDaysIcon,
  ClockIcon,
  Cog6ToothIcon,
  ComputerDesktopIcon,
  PaperAirplaneIcon,
  ShoppingBagIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import Hamburger from "hamburger-react";
import React, { useEffect, useRef, useState } from "react";
import { FcOnlineSupport } from "react-icons/fc";
import { IoMdLogOut } from "react-icons/io";
import { MdOutlineInventory } from "react-icons/md";
import { PiUserListLight } from "react-icons/pi";
import homServiceIcon from "@assets/images/homeservice.svg"
import { RiLuggageCartFill } from "react-icons/ri";
import { Link, useLocation } from "react-router-dom";
// import CopyCurlLogger from "../CopyCurlLogger";
import { IStaticSidebarComponentProps } from "../interface";
import StaticSidebarNavOption from "./NavOption";
import { BiTransfer } from "react-icons/bi";
import { IoDocumentTextOutline, IoFlaskOutline } from "react-icons/io5";
import RBACFlag from "../RBACFlag";
import { LuListTodo } from "react-icons/lu";

export const NAV_ICON_CLASS =
  "w-4 h-4 md:w-4 md:h-4 lg:w-4 lg:h-4 xl:w-5 xl:h-5 2xl:h-6 2xl:w-6";

export const NAV_ICONS = {
  CUSTOMER_PROFILE: <UserCircleIcon className={NAV_ICON_CLASS} />,
  APPOINTMENTS: <ClockIcon className={NAV_ICON_CLASS} />,
  ROTA: <ComputerDesktopIcon className={NAV_ICON_CLASS} />,
  PATIENT_WAITING_LIST: (
    <PiUserListLight className={NAV_ICON_CLASS} />
  ),
  AT_HOME_SERVICES: (
    <img src={homServiceIcon} className={NAV_ICON_CLASS} />
  ),

  STAFF_CALENDAR: <CalendarDaysIcon className={NAV_ICON_CLASS} />,
  ORDER_MANAGEMENT: <ShoppingBagIcon className={NAV_ICON_CLASS} />,
  QC_TRANSFER_ORDER: (
    <RiLuggageCartFill className={NAV_ICON_CLASS} />
  ),
  CART_MANAGEMENT: <RiLuggageCartFill className={NAV_ICON_CLASS} />,
  INVENTORY: <MdOutlineInventory className={NAV_ICON_CLASS} />,
  ACCOUNTING: <BiTransfer className={NAV_ICON_CLASS} />,
  DAY_CLOSING: <LuListTodo className={NAV_ICON_CLASS} />,
  LAB_REPORT: <IoFlaskOutline className={NAV_ICON_CLASS} />,
  LAB_REPORT_INTERPRETATION: <IoDocumentTextOutline className={NAV_ICON_CLASS} />,
  TRANSFER_ORDERS: <PaperAirplaneIcon className={NAV_ICON_CLASS} />,
  CLINIC_TO_DOCTOR_TRANSFER: <PaperAirplaneIcon className={NAV_ICON_CLASS} />,
};

const StaticSidebarComponent: React.FC<IStaticSidebarComponentProps> = ({ toggleSidebar }) => {
  const { getRouteKey } = useRouter();
  const { logVMSClick } = useVMSClickLog(VMS_SCREEN_IDS.SIDEBAR);
  const location = useLocation();
  const configuration = useConfiguration();
  const [pinnedItem, setPinnedItem] = useState<CustomerProfileBookmarkType>(null);
  const { status: pinProfileStatus } = useTriggerState("pin_profile");
  const { VETIC_CHAR_LOGO_WHITE } = useImageConstants();
  const { desktop } = useAppSelector((state) => state.root.UIStates.sidebar);
  const {
    env: { version },
  } = useConfiguration();
  const prevPathRef = useRef(location.pathname);
  // clinic_ops: logout lives here (below Need Help) instead of the navbar.
  const isClinicOpsUser = useIsClinicOpsUser();
  const handleLogout = useLogoutAction();

  useEffect(() => {
    if (localStorage.getItem(configuration.localStorage.PINNED_PROFILES.key)) {
      setPinnedItem(
        JSON.parse(localStorage.getItem(configuration.localStorage.PINNED_PROFILES.key)) as CustomerProfileBookmarkType,
      );
    }
  }, []);

  useEffect(() => {
    if (pinProfileStatus === "DISABLE") return;
    setPinnedItem(
      JSON.parse(localStorage.getItem(configuration.localStorage.PINNED_PROFILES.key)) as CustomerProfileBookmarkType,
    );
  }, [pinProfileStatus]);

  useEffect(() => {
    if (location.pathname === prevPathRef.current) return;
    const isMobileViewport = window.innerWidth < 1024;
    if (isMobileViewport && desktop?.show) {
      toggleSidebar(false);
    }
    prevPathRef.current = location.pathname;
  }, [location.pathname, desktop?.show, toggleSidebar]);

  return (
    <>
      {desktop?.show && (
        <motion.div
          initial={{ x: -300 }}
          animate={{ x: 0 }}
          exit={{ x: -300 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="z-50 lg:z-50 fixed lg:inset-y-0 lg:flex lg:flex-col bg-gray-900 md:w-60 lg:w-60 xl:w-60 2xl:w-72  h-full"
        >
          <div className="flex flex-col gap-y-1 2xl:gap-y-2  bg-gray-900 px-3 md:px-4 xl:px-3 2xl:px-5 pb-4 overflow-y-auto grow h-[650px] md:h-auto lg:h-auto  xl:h-auto 2xl:h-auto">
            <div className="flex justify-between items-center w-full">
              <div className="flex items-center h-16 shrink-0">
                <img
                  className=" w-auto  h-7  lg:w-4 lg:h-4 xl:w-6 xl:h-8 2xl:w-8 2xl:h-8"
                  src={VETIC_CHAR_LOGO_WHITE}
                  alt="Vetic"
                />
              </div>
              <div className="-mr-3">
                <Hamburger
                  color="white"
                  size={20}
                  toggled={desktop?.show}
                  onToggle={() => {
                    logVMSClick(VMS_CLICK_EVENTS.SIDEBAR_CLOSE_CLICK, "Close Navigation");
                    toggleSidebar(false);
                  }}
                />
              </div>
            </div>
            <div className="flex flex-col flex-1  overflow-y-auto" style={{ scrollbarGutter: "stable" }}>
              <nav className="flex flex-col flex-1">
                <ul role="list" className="flex flex-col flex-1 gap-y-7">
                  <li>
                    <ul role="list" className="space-y-2 2xl:space-y-3 w-full">
                      {Object.keys(NAVIGATION_ROUTES).map((parentKey) => (
                        <StaticSidebarNavOption
                          navIcons={NAV_ICONS}
                          key={parentKey}
                          navOption={NAVIGATION_ROUTES[parentKey]}
                          primaryModuleKey={parentKey}
                        />
                      ))}
                    </ul>
                  </li>

                  <li className="mt-auto">
                    {/* <CopyCurlLogger /> */}
                    <div className="flex items-center gap-2">
                      <RBACFlag actionId="CONFIGURATION_MODULE">
                        <Link
                          to={getRouteKey("CONFIGURATION", "url")}
                          onClick={() => logVMSClick(VMS_CLICK_EVENTS.SIDEBAR_CONFIGURATION_CLICK, "Configuration")}
                          className="flex gap-x-3 hover:bg-gray-800  p-2 rounded-md font-semibold items-center text-gray-400 text-sm hover:text-white leading-6 group text-[10px] md:text-xs lg:text-xs xl:text-xs 2xl:text-sm"
                        >
                          <Cog6ToothIcon
                            className={`${NAV_ICON_CLASS} shrink-0`}
                            aria-hidden="true"
                          />
                          <span className="text-[10px] md:text-xs lg:text-xs xl:text-xs 2xl:text-sm">
                            Configuration
                          </span>
                        </Link>
                      </RBACFlag>
                      {/* <span className="text-white text-[11px] md:text-xs lg:text-xs xl:text-xs 2xl:text-sm ">
                      {version}
                    </span> */}
                    </div>
                    <Link
                      to={getRouteKey("SERVECE_DESK_RAISE_ISSUE_LIST", "url")}
                      onClick={() => logVMSClick(VMS_CLICK_EVENTS.SIDEBAR_NEED_HELP_CLICK, "Need Help")}
                      className="flex gap-x-3 hover:bg-gray-800  p-2 rounded-md font-semibold text-gray-400 text-sm hover:text-white leading-6 group text-[10px] md:text-xs lg:text-xs xl:text-xs 2xl:text-sm"
                    >
                      <FcOnlineSupport
                        className={`${NAV_ICON_CLASS} shrink-0`}
                        aria-hidden="true"
                      />
                      <span className="text-[11px] md:text-xs lg:text-xs xl:text-xs 2xl:text-sm">Need Help</span>
                    </Link>
                    {isClinicOpsUser && (
                      <div className="mt-2 pt-2 border-t border-gray-800">
                        <button
                          type="button"
                          onClick={handleLogout}
                          className="flex gap-x-3 hover:bg-gray-800 p-2 rounded-md w-full font-semibold text-gray-400 text-sm hover:text-white leading-6 group text-[10px] md:text-xs lg:text-xs xl:text-xs 2xl:text-sm"
                        >
                          <IoMdLogOut className={`${NAV_ICON_CLASS} shrink-0 text-red-500`} aria-hidden="true" />
                          <span className="text-[11px] md:text-xs lg:text-xs xl:text-xs 2xl:text-sm">Logout</span>
                        </button>
                      </div>
                    )}
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
};

export default StaticSidebarComponent;
